import React, { useState, useEffect } from "react";
import { Button } from "./ResultsComponents";
import { MdClose } from "react-icons/md";

const AddCAModal = ({ open, onClose, onSave, existingTitles = [], maxScoreRemaining = 0 }) => {
  const [title, setTitle] = useState("");
  const [maxScore, setMaxScore] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle("");
      setMaxScore("");
      setDescription("");
      setError("");
    }
  }, [open]);

  const handleSave = () => {
    // Reset error
    setError("");

    // Validate title
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    // Check for duplicate titles
    if (existingTitles.includes(title.trim())) {
      setError("Title must be unique");
      return;
    }

    // Validate max score
    const scoreValue = Number(maxScore);
    if (!maxScore || isNaN(scoreValue) || scoreValue <= 0) {
      setError("Max Score must be a positive number");
      return;
    }

    if (scoreValue > maxScoreRemaining) {
      setError(`Max Score cannot exceed remaining CA weight (${maxScoreRemaining})`);
      return;
    }

    // All validations passed
    const payload = {
      title: title.trim(),
      max_score: scoreValue,
    };
    if (description.trim()) {
      payload.description = description.trim();
    }
    onSave(payload);

    // Reset form
    setTitle("");
    setMaxScore("");
    setDescription("");
    setError("");
  };

  const handleClose = () => {
    setTitle("");
    setMaxScore("");
    setDescription("");
    setError("");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-md relative mx-4">
        <button 
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors" 
          onClick={handleClose}
        >
          <MdClose size={24} />
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-gray-800">Add Continuous Assessment</h2>
        
        {/* Title Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Assessment Title <span className="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
            value={title} 
            onChange={e => setTitle(e.target.value)} 
            maxLength={30} 
            placeholder="e.g. CA 1, Quiz 1, Assignment 1" 
          />
          <p className="text-xs text-gray-500 mt-1">{title.length}/30 characters</p>
        </div>

        {/* Max Score Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Maximum Score <span className="text-red-500">*</span>
          </label>
          <input 
            type="number" 
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
            value={maxScore} 
            onChange={e => setMaxScore(e.target.value)} 
            min={1} 
            max={maxScoreRemaining}
            placeholder="Enter score"
          />
          <p className="text-xs text-gray-500 mt-1">
            Available: {maxScoreRemaining} points remaining
          </p>
        </div>

        {/* Description Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Description <span className="text-gray-400">(Optional)</span>
          </label>
          <input 
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none" 
            value={description} 
            onChange={e => setDescription(e.target.value)} 
            maxLength={100} 
            placeholder="Brief description of this assessment..."
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">{description.length}/100 characters</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <span className="inline-block w-4 h-4 bg-red-100 rounded-full mr-2 flex items-center justify-center">
                <span className="text-xs text-red-600">×</span>
              </span>
              <span className="text-red-600 text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button 
            variant={2} 
            onClick={handleClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            className="flex-1"
            disabled={!title.trim() || !maxScore}
          >
            Add Assessment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCAModal;